import type {Account, Listing, ReservationDocument} from "../generated/models";

export const DATE_FORMAT = "YYYY-MM-DD";

export type ReservationStatus = ReservationDocument["status"];
export type ReservationSource = ReservationDocument["source"];
export type ReservationDisplaySource = Exclude<ReservationDocument["source"], "HomeAway"> | "VRBO";
export type ReservationProviderSource = Exclude<ReservationDocument["source"], "internal">;
export type AccountType = Account["type"];

export const originalChannels = ["Airbnb", "HomeAway", "Booking", "Houfy"] as const;
export const providerChannels = [...originalChannels, "ChannelConnector"] as const;
export const pricingChannels = [...originalChannels, "ChannelConnector", "direct"] as const;
export type PricingChannels = (typeof pricingChannels)[number];
export type Channels = (typeof providerChannels)[number];
export const channels = providerChannels;

// NOTE: setting `as const satisfies readonly IntegrationsType[]`
// the babel which is used in metro config might not be able to understand it
export const integrations = ["August", "Seam"] as const;
export type IntegrationsType = (typeof integrations)[number];

export const internal = "internal";
export const direct = "direct";

export const reservationSources: ReservationSource[] = [...providerChannels, internal, direct];

export const validListingStatuses = ["listed", "unlisted"] as const;

export type ValidListingStatuses = (typeof validListingStatuses)[number];

export const listingStatuses = [...validListingStatuses, "not found", "pending"] as const;

export type ListingStatuses = (typeof listingStatuses)[number];

export const priceSourceValues = ["Amount", "HomeAway", "Houfy", "PriceLabs", "Beyond Pricing"];

export const bookingWindowValues = [30, 45, 60, 75, 90, 180, 365, 547, 0];

export const reservationStatuses: ReservationStatus[] = [
    "inquiry",
    "not_possible",
    "preapproved",
    "denied",
    "timedout",
    "pending",
    "checkpoint",
    "awaiting_payment",
    "accepted",
    "cancelled",
    "deny",
    "timeout",
    "pending_payment",
    "at_checkpoint",
    "checkpoint_voided",
    "pending_verification"
];

export const blockingReservationStatuses = ["pending_payment", "accepted"];

export type EventReservationStatus = "accepted" | "inquiry" | "timedout" | "pending" | "cancelled";
export const eventReservationStatus: EventReservationStatus[] = [
    "accepted",
    "inquiry",
    "timedout",
    "pending",
    "cancelled"
];

export function isValidStatus(status: string) {
    return [
        "inquiry",
        "pending",
        "accepted",
        "cancelled",
        "timedout",
        "preapproved",
        "not_possible",
        "denied",
        "awaiting_payment",
        "pending_payment",
        "withdrawn"
    ].includes(status);
}

export const permanentlyFailedMessageStatus = [
    "review_already_left",
    "review_not_left_cohost",
    "review_failed",
    "pre-approval_dates_not_available",
    "not_us_ca_phone_number",
    "not_a_valid_phone_number",
    "phone_number_violates_blacklist_rule",
    "invalid_email",
    "email_rejected",
    "user_subscription_inactive",
    "airbnb_account_logged_out",
    "homeaway_account_logged_out",
    "booking_account_logged_out",
    "houfy_account_logged_out",
    "message_failed_to_send_within_24_hours"
];

export const temporaryFailedMessageStatus = [
    "review_sent",
    "review_pending",
    "pending_host_review"
];

export const failedMessageStatus = [
    ...permanentlyFailedMessageStatus,
    "do_not_have_permission",
    "send_message_failed",
    "sms_message_failed",
    "webhook_post_failed"
];

export const successMessageStatus = ["sent"];

export const disabledMessageStatus = ["disabled"];

export const enabledMessageStatus = ["enabled"];

export const messageCompleteStatus = [
    ...successMessageStatus,
    ...disabledMessageStatus,
    ...permanentlyFailedMessageStatus
];

export const messageStatus = [
    ...successMessageStatus,
    ...disabledMessageStatus,
    ...failedMessageStatus,
    ...enabledMessageStatus,
    ...temporaryFailedMessageStatus
] as const;

export type MessageStatus = (typeof messageStatus)[number];

export const UTC_TIMEZONE = "Etc/UTC";

export const languages = [
    "id",
    "ms",
    "ca",
    "da",
    "de",
    "en",
    "es",
    "el",
    "fr",
    "hr",
    "it",
    "hu",
    "nl",
    "no",
    "pl",
    "pt",
    "fi",
    "sv",
    "tr",
    "is",
    "cs",
    "ru",
    "he",
    "ar",
    "th",
    "ja",
    "zh",
    "zh-TW",
    "ko"
] as const;

export type Languages = (typeof languages)[number];

export const secondInMs = 1000;
export const minuteInMs = 60 * secondInMs;
export const hourInMs = 60 * minuteInMs;
export const dayInMs = 24 * hourInMs;

export const yearInDays = 365;
export const twoYearsInDays = 2 * yearInDays;

export const apiKeyNames = ["OpenAI"] as const;
export const maxImagesOnMessageRule = 5;
export const maxImagesOnListing = 50;

export const TEST_USERS = ["604f9301b7ef9d23ff3c7798"];

export const requiredListingFields = [
    "airbnbName",
    "notes",
    // "fees", // These are not required, they can be empty
    // "taxes", // These are not required, they can be empty
    "bedrooms",
    "bathrooms",
    "images",
    "bedroomConfiguration",
    "attributesWithQuantity",
    "airbnbAddress",
    "timeZone",
    "currency",
    "airbnbCheckInTime",
    "airbnbCheckOutTime"
] as (keyof Listing)[];

export type ApiKeyName = (typeof apiKeyNames)[number];

export const rateNames = [
    // PRICE
    "Base price",
    // DEPOSIT
    "Security deposit",
    // FEES
    "Cleaning fee",
    "Short term cleaning fee",
    "Security deposit",
    "Community fee",
    "Management fee",
    "Resort fee",
    "Pet fee",
    "Gas fee",
    "Electricity fee",
    "Water fee",
    "Heating fee",
    "Linens fee",
    "Air conditioning fee",
    "Utility fee",
    "Channel guest fee",
    "Channel guest VAT",
    "Channel host fee",
    "Channel host VAT",
    "Payment processing fee",
    "Additional guest fee",
    // PASS THROUGH TAXES
    "Hotel tax",
    "Lodging tax",
    "Room tax",
    "Tourist tax",
    "Transient occupancy tax", // "City tax"?
    "Sales tax",
    "VAT/GST",
    "Tourism Assessment/Fee",
    "Total taxes", // This is added because Houfy only returns a total tax amount
    // COLLECTED TAXES
    "Accommodations Tax",
    "General Sales and Use Tax"
];

export const rateTypes = ["fee", "tax", "price", "deposit"] as const;
export type RateTypes = (typeof rateTypes)[number];

export const paidBy = ["host", "guest"] as const;
export type PaidBy = (typeof paidBy)[number];

// there are cases that we need to check specific tests user such as Airbnb
// not sure if this should be an env variable
export const testUsers = ["testtest@hosttools.com"];
export const BETA_HOST = "https://beta.hosttools.com";
export const PROD_HOST = "https://app.hosttools.com";

export const helpLink = "https://help.hosttools.com";
export const supportEmail = "support@hosttools.com";
export const systemStatusLink = "https://hosttools.instatus.com/";

export const noop = () => {};

export const MESSAGING_MONTHLY_PRICES = [
    30, 40, 48, 56, 64, 72, 80, 88, 96, 103, 110, 117, 124, 131, 138, 145, 152, 159, 166, 172, 178,
    184, 190, 196, 202, 208, 214, 220, 226, 232, 238, 244, 250, 256, 262, 268, 274, 280, 286, 292,
    298, 304, 310, 316, 322, 328, 334, 340, 345, 350, 355, 360, 365, 370, 375, 380, 385, 390, 395,
    400, 405, 410, 415, 420, 425, 430, 435, 440, 445, 450, 455, 460, 465, 470, 475, 480, 485, 490,
    495, 500, 505, 510, 515, 520, 525, 530, 535, 540, 545, 550, 555, 560, 565, 570, 575, 580, 585,
    590, 595, 600, 605, 610, 615, 620, 625, 630, 635, 640, 645, 650, 655, 660, 665, 670, 675, 680,
    685, 690, 695, 700, 705, 710, 715, 720, 725, 730, 735, 740, 745, 750, 755, 760, 765, 770, 775,
    780, 785, 790, 795, 800
];

export const MESSAGING_YEARLY_PRICES = [
    28, 37, 44, 51, 58, 65, 72, 79, 86, 92, 98, 104, 110, 116, 122, 128, 134, 140, 146, 151, 156,
    161, 166, 171, 176, 181, 186, 191, 196, 201, 206, 211, 216, 221, 226, 231, 236, 241, 246, 251,
    256, 261, 266, 271, 276, 281, 286, 291, 296, 301, 306, 311, 316, 321, 326, 331, 336, 341, 346,
    351, 356, 361, 366, 371, 376, 381, 386, 391, 396, 401, 406, 411, 416, 421, 426, 431, 436, 441,
    446, 451, 456, 461, 466, 471, 476, 481, 486, 491, 496, 501, 506, 511, 516, 521, 526, 531, 536,
    541, 546, 551, 556, 561, 566, 571, 576, 581, 586, 591, 596, 601, 606, 611, 616, 621, 626, 631,
    636, 641, 646, 651, 656, 661, 666, 671, 676, 681, 686, 691, 696, 701, 706, 711, 716, 721, 726,
    731, 736, 741, 746, 751
];

export const PRO_MONTHLY_PRICES = [
    32, 43, 51, 59, 67, 75, 83, 91, 99, 106, 113, 120, 127, 134, 141, 148, 155, 162, 169, 175, 181,
    187, 193, 199, 205, 211, 217, 223, 229, 235, 241, 247, 253, 259, 265, 271, 277, 283, 289, 295,
    301, 307, 313, 319, 325, 331, 337, 343, 348, 353, 358, 363, 368, 373, 378, 383, 388, 393, 398,
    403, 408, 413, 418, 423, 428, 433, 438, 443, 448, 453, 458, 463, 468, 473, 478, 483, 488, 493,
    498, 503, 508, 513, 518, 523, 528, 533, 538, 543, 548, 553, 558, 563, 568, 573, 578, 583, 588,
    593, 598, 603, 608, 613, 618, 623, 628, 633, 638, 643, 648, 653, 658, 663, 668, 673, 678, 683,
    688, 693, 698, 703, 708, 713, 718, 723, 728, 733, 738, 743, 748, 753, 758, 763, 768, 773, 778,
    783, 788, 793, 798, 803
];

export const PRO_YEARLY_PRICES = [
    30, 40, 48, 56, 64, 72, 80, 88, 96, 103, 110, 117, 124, 131, 138, 145, 152, 159, 166, 172, 178,
    184, 190, 196, 202, 208, 214, 220, 226, 232, 238, 244, 250, 256, 262, 268, 274, 280, 286, 292,
    298, 304, 310, 316, 322, 328, 334, 340, 345, 350, 355, 360, 365, 370, 375, 380, 385, 390, 395,
    400, 405, 410, 415, 420, 425, 430, 435, 440, 445, 450, 455, 460, 465, 470, 475, 480, 485, 490,
    495, 500, 505, 510, 515, 520, 525, 530, 535, 540, 545, 550, 555, 560, 565, 570, 575, 580, 585,
    590, 595, 600, 605, 610, 615, 620, 625, 630, 635, 640, 645, 650, 655, 660, 665, 670, 675, 680,
    685, 690, 695, 700, 705, 710, 715, 720, 725, 730, 735, 740, 745, 750, 755, 760, 765, 770, 775,
    780, 785, 790, 795, 800
];
